import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { CHAIN_IDS, solana } from '../constants/chain';
import { SourcesList, useLiquiditySourcesStore } from '../store/liquiditySources';
import { SwapSourcesResponse } from '../utils/0x.types';
import { fetcher } from '../utils/fetcher';
import { routes } from '../utils/routes';

interface SourcesResponse {
  /** Total number of sources. */
  total: number;
  /** Selected number of sources. */
  selected: number;
  /** A `SourcesList` object keyed by name. */
  sources: SourcesList;
  isLoading: boolean;
  error: Error | null;
}

export const sourcesQueryKey = (chainId: number) => ['liquidity-sources', chainId] as const;

/**
 * Returns a list of liquidity sources for the provided chain.
 * @param chainId - The chain ID of the current network (defaults to ethereum).
 * @param enabled - Whether the sources request should run.
 */
export const useSources = (
  chainId: number = CHAIN_IDS.MAINNET,
  enabled: boolean = true,
): SourcesResponse => {
  let total = 0,
    selected = 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sources: SourcesList = useMemo(() => ({}), [chainId]); // only bust cache on network change
  const existingSources = useLiquiditySourcesStore(
    (state) => state.liquiditySources?.[chainId]?.sources,
  );

  const { data, isLoading, error } = useQuery<SwapSourcesResponse, Error>({
    queryKey: sourcesQueryKey(chainId),
    queryFn: () => fetcher(`${routes.api.SOURCES}?chainId=${chainId}`),
    enabled: enabled && chainId !== solana.id,
  });

  if (data && data.sources) {
    const records = data.sources.filter((record) => record !== 'MultiHop');

    total = records.length;
    records.forEach((record) => {
      // prefer existing source setting
      sources[record] = existingSources?.[record] ?? {
        displayName: record.trim().replace(/_/g, ' '), // replace underscores with spaces
        enabled: true, // "opt-out" behavior by default
      };

      if (sources[record].enabled) selected++;
    });
  }

  return {
    total,
    selected,
    sources,
    isLoading,
    error,
  };
};
